import { useState } from "react";
import Navbar from "@/components/Common/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
// import { Separator } from "@/components/ui/separator";

import { useProfileManagement } from "../hooks/useProfileManagement";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useChangePassword } from "../api/auth.queries";
import { extractErrorMessage } from "@/lib/errors";
import TokenContainer from "../components/Auth/AuthTokenContainer";
import { ProfileForm } from "../components/Auth/AuthProfileForm";
import { useAuthStore } from "../store/useAuthStore";
import { Spinner } from "@/components/ui/spinner";

const commonPasswords = [
  "12345678",
  "password",
  "password123",
  "qwerty",
  "abc123",
  "abcd1234",
  "letmein",
  "iloveyou",
  "admin123",
];
const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters long")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/\d/, "Password must contain at least one number")
      .regex(
        /[!@#$%^&*(),.?":{}|<>_]/,
        "Password must contain at least one special character",
      )
      .refine(
        (val) => !commonPasswords.includes(val.toLowerCase()),
        "Password is too common, please choose another one",
      ),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ChangePasswordSchema = z.infer<typeof changePasswordSchema>;

export default function Settings() {
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Use the new profile management hook
  const profileManagement = useProfileManagement();

  const form = useForm<ChangePasswordSchema>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const { mutate: changePassword, isPending } = useChangePassword();
  const { setError } = form;

  function onSubmit(values: ChangePasswordSchema) {
    changePassword(
      {
        current_password: values.currentPassword,
        new_password: values.newPassword,
        app_id: "68958c254d1efc253f7380e0",
      },
      {
        onSuccess: () => {
          form.reset();
        },
        onError: (err) => {
          const message = extractErrorMessage(err);
          if (message.includes("current_password")) {
            setError("currentPassword", { type: "server", message });
          } else if (message.includes("new_password")) {
            setError("newPassword", { type: "server", message });
          } else {
            setError("root", { type: "server", message });
          }
        },
      },
    );
  }

  const { isHashKeyUser } = useAuthStore();

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex w-full flex-col gap-3 p-3 px-6">
        <Navbar />
      </div>

      <div className="flex items-start justify-center overflow-hidden p-6 pt-0">
        <div className="flex h-full w-[1180px] gap-[13px]">
          {/* API Tokens Sidebar */}
          <aside className="flex h-[calc(100vh-105px)] w-[35%] flex-col overflow-hidden rounded-[12px] bg-white py-5 pr-2 pl-5">
            {/* Profile Header Card
            <Card className="mb-6 flex-shrink-0 !p-0 !shadow-none">
              <CardContent className="p-3">
                <div className="flex items-center gap-4 lg:gap-6">
                  <div className="relative flex-shrink-0">
                    <div className="flex h-[60px] w-[60px] items-center justify-center rounded-full border border-neutral-300 bg-gradient-to-br from-blue-50 to-indigo-100 lg:h-[72px] lg:w-[72px]">
                      {user?.profile_picture ? (
                        <img
                          src={user.profile_picture}
                          alt="profile"
                          className="h-full w-full rounded-full object-cover"
                        />
                      ) : (
                        <i className="ri-user-line text-[30px] font-normal text-neutral-800 lg:text-[36px]"></i>
                      )}
                    </div>
                    <button className="absolute -right-1 -bottom-1 rounded-full border border-neutral-300 bg-neutral-100 p-1.5 shadow-sm transition-shadow hover:shadow-md">
                      <i className="ri-edit-line text-[14px] font-normal text-muted lg:text-[16px]"></i>
                    </button>
                  </div>

                  <div className="min-w-0">
                    <h2 className="truncate text-[18px] leading-normal font-bold -tracking-wide text-neutral-800 lg:text-[20px]">
                      {user?.first_name} {user?.last_name}
                    </h2>
                    <p className="truncate text-sm leading-normal font-medium text-neutral-800">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </CardContent>
              <Separator className="mt-4" />
            </Card> */}
            {/* API Tokens Card */}
            <Card className="flex h-full w-full flex-col overflow-hidden !p-0 pr-3 !shadow-none">
              <CardContent className="scrollbar-thin flex h-full flex-col overflow-y-auto p-0">
                <TokenContainer />
              </CardContent>
            </Card>
          </aside>

          {/* Main Content */}
          <main className="flex h-[calc(100vh-105px)] w-[60%] flex-col overflow-hidden rounded-[12px] bg-white py-5 pr-2 pl-5">
            <div className="scrollbar-thin space-y-10 overflow-y-auto pr-3">
              {/* Account Information */}
              <section>
                <Card className="space-y-3 !p-0 !shadow-none">
                  <CardHeader>
                    <CardTitle className="text-[20px]">
                      Account Information
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="w-full space-y-6">
                    <ProfileForm
                      originalData={profileManagement.originalData}
                      onSave={profileManagement.handleSave}
                      onCancel={profileManagement.handleCancel}
                      isEditing={profileManagement.isEditing}
                      isLoading={profileManagement.isLoading}
                      onEditToggle={profileManagement.handleEditToggle}
                      isHashKeyUser={isHashKeyUser()}
                    />
                  </CardContent>
                </Card>
              </section>

              {/* Change Password */}
              <section>
                <Card className="space-y-3 !p-0 !shadow-none">
                  <CardHeader>
                    <CardTitle className="text-[20px]">
                      Change Password
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    <Form {...form}>
                      <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-6"
                      >
                        {/* Current Password */}
                        <FormField
                          control={form.control}
                          name="currentPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-bold text-neutral-800">
                                Current Password{" "}
                                <span className="text-red-500">*</span>
                              </FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter your current password"
                                    variant="filled"
                                    autoComplete="current-password"
                                    className="pr-10 [&::-ms-clear]:hidden [&::-ms-reveal]:hidden"
                                    {...field}
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute top-1/2 right-2 h-8 w-8 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    onClick={() =>
                                      setShowPassword(!showPassword)
                                    }
                                  >
                                    <i
                                      className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-neutral-800`}
                                    ></i>
                                  </Button>
                                </div>
                              </FormControl>
                              <FormMessage className="min-h-[16px] text-xs text-red-600" />
                            </FormItem>
                          )}
                        />

                        {/* New Password */}
                        <FormField
                          control={form.control}
                          name="newPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-bold text-neutral-800">
                                New Password{" "}
                                <span className="text-red-500">*</span>
                              </FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    type={showNewPassword ? "text" : "password"}
                                    placeholder="Enter your new password"
                                    variant="filled"
                                    className="pr-10 [&::-ms-clear]:hidden [&::-ms-reveal]:hidden"
                                    {...field}
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute top-1/2 right-2 h-8 w-8 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    onClick={() =>
                                      setShowNewPassword(!showNewPassword)
                                    }
                                  >
                                    <i
                                      className={`${showNewPassword ? "ri-eye-off-line" : "ri-eye-line"} text-neutral-800`}
                                    ></i>
                                  </Button>
                                </div>
                              </FormControl>
                              <FormMessage className="min-h-[16px] text-xs text-red-600" />
                            </FormItem>
                          )}
                        />

                        {/* Confirm Password */}
                        <FormField
                          control={form.control}
                          name="confirmPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-bold text-neutral-800">
                                Confirm New Password{" "}
                                <span className="text-red-500">*</span>
                              </FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    type={
                                      showConfirmPassword ? "text" : "password"
                                    }
                                    placeholder="Confirm your new password"
                                    variant="filled"
                                    className="pr-10 [&::-ms-clear]:hidden [&::-ms-reveal]:hidden"
                                    {...field}
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute top-1/2 right-2 h-8 w-8 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    onClick={() =>
                                      setShowConfirmPassword(
                                        !showConfirmPassword,
                                      )
                                    }
                                  >
                                    <i
                                      className={`${showConfirmPassword ? "ri-eye-off-line" : "ri-eye-line"} text-neutral-800`}
                                    ></i>
                                  </Button>
                                </div>
                              </FormControl>
                              <FormMessage className="min-h-[16px] text-xs text-red-600" />
                            </FormItem>
                          )}
                        />
                        {/* <div className="rounded-lg border border-[#3B82F6] bg-[#EFF6FF] px-4 py-3">
                          <h4 className="mb-2 text-sm font-bold text-neutral-800">
                            Password Requirements
                          </h4>
                          <ul className="space-y-1 text-sm font-medium text-neutral-800">
                            <li>• At least 8 characters long</li>
                            <li>• Include uppercase and lowercase letters</li>
                            <li>• Include at least one number</li>
                            <li>• Include at least one special character</li>
                          </ul>
                        </div> */}
                        <Button
                          type="submit"
                          disabled={isPending || isHashKeyUser()}
                          className="flex h-[48px] w-full items-center justify-center gap-1 py-3 hover:scale-none sm:w-[200px]"
                        >
                          {isPending ? (
                            <div className="flex h-full items-center justify-center text-white">
                              <Spinner size="sm" className="stroke-white!" />
                            </div>
                          ) : (
                            "Save Changes"
                          )}
                        </Button>
                      </form>
                    </Form>
                  </CardContent>
                </Card>
              </section>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
